import React, { useEffect, useState } from "react";
import Modal from "src/components/ui/Modal";
import Dropdown from "src/components/ui/Dropdown";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import Checkbox from "src/components/ui/Checkbox";
import Textarea from "src/components/ui/Textarea";
import { toast } from "react-toastify";
import planApis from "src/api/plan";
import tenantApis from "src/api/tenant";
import { Plan } from "src/types/plan.types";
import { currencyFormatter } from "src/utils/common";

interface ExtendPlanModalProps {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ExtendPlanModal: React.FC<ExtendPlanModalProps> = ({
  open,
  tenantId,
  onClose,
  onSuccess,
}) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentMode, setPaymentMode] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [startFromToday, setStartFromToday] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadPlans();
      setSelectedPlan(null);
      setPaymentMethod("cash");
      setPaymentMode("");
      setReferenceId("");
      setRemarks("");
      setStartFromToday(false);
    }
  }, [open]);

  const loadPlans = async () => {
    try {
      const response = await planApis.getPlanList({
        page: 1,
        limit: 1000,
        status: "active",
      });
      const list = response?.data || [];
      setPlans(list);
    } catch (error) {
      console.error("Error loading plans:", error);
      toast.error("Failed to load plans");
    }
  };

  console.log("ExtendPlanModal", tenantId);

  const handleSubmit = async () => {
    if (!selectedPlan) {
      toast.error("Please select a plan");
      return;
    }

    if (paymentMethod === "online") {
      if (!paymentMode) {
        toast.error("Please select a payment mode");
        return;
      }
      if (!referenceId) {
        toast.error("Reference Id is required for online payments");
        return;
      }
    }

    setLoading(true);
    try {
      await tenantApis.extendTenantPlan({
        id: tenantId,
        data: {
          planId: selectedPlan?._id,
          paymentMethod,
          paymentMode: paymentMethod === "online" ? paymentMode : undefined,
          referenceId: paymentMethod === "online" ? referenceId : undefined,
          remarks: remarks.trim() || undefined,
          startFromToday,
        },
      });
      toast.success("Plan extended successfully");
      onClose();
      onSuccess();
    } catch (error: any) {
      console.error("Error extending plan:", error);
      toast.error(error?.response?.data?.message || "Failed to extend plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Extend Plan">
      <div className="space-y-4">
        <Dropdown
          label="Select Plan"
          value={selectedPlan?._id || ""}
          onChange={(selectedPlanId) => {
            const filterSelectedPlan = plans?.filter(
              (plan) => plan?._id === selectedPlanId
            );
            setSelectedPlan(
              filterSelectedPlan?.length > 0 ? filterSelectedPlan[0] : null
            );
          }}
          options={plans.map((plan) => ({
            label: `${plan.planName} (${plan.planCode})`,
            value: plan._id || "",
          }))}
          fullWidth
        />

        {selectedPlan && (
          <div className="space-y-4">
            <div className="text-sm font-medium text-gray-700">
              Plan Details
            </div>
            <div className="mt-1 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <div className="text-gray-500">Plan Name: </div>
                  <div className="font-semibold text-gray-900">
                    {selectedPlan?.planName}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Plan Code: </div>
                  <div className="font-semibold text-gray-900">
                    {selectedPlan?.planCode}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Plan Duration: </div>
                  <div className="font-semibold text-gray-900">
                    {selectedPlan?.durationInDays} days
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Plan Price:</div>
                  <div className="font-semibold text-gray-900">
                    {currencyFormatter(selectedPlan?.price)}
                  </div>
                </div>
              </div>
            </div>

            <Checkbox
              label="Start from today (expire current plan)"
              checked={startFromToday}
              onChange={(e) => setStartFromToday(e.target.checked)}
            />
          </div>
        )}

        <Dropdown
          label="Payment Method"
          value={paymentMethod}
          onChange={setPaymentMethod}
          options={[
            { label: "Cash", value: "cash" },
            { label: "Online", value: "online" },
          ]}
          fullWidth
        />

        {paymentMethod === "online" && (
          <>
            <Dropdown
              label="Payment Mode"
              value={paymentMode}
              onChange={setPaymentMode}
              options={[
                { label: "PhonePe", value: "phonepay" },
                { label: "GPay", value: "gpay" },
                { label: "Paytm", value: "paytm" },
                { label: "Other", value: "other" },
              ]}
              fullWidth
            />
            <Input
              label="Reference Id"
              name="referenceId"
              value={referenceId}
              onChange={(e) => setReferenceId(e.target.value)}
              placeholder="Enter Reference Id"
            />
          </>
        )}

        <Textarea
          label="Remarks"
          name="remarks"
          value={remarks}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setRemarks(e.target.value)
          }
          placeholder="Add remarks (optional)"
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} isLoading={loading}>
            Extend Plan
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExtendPlanModal;
